import type { TemplateSettings } from 'modules/map/components/daily-dish/types';
import type { LookStorageRecord } from 'modules/map/components/daily-dish/look/types';

export const LOOK_STORAGE_PREFIX = 'gate.dailyDish.look.v1:';

export const lookStorageKey = (dateFrom: string): string => `${LOOK_STORAGE_PREFIX}${dateFrom}`;

export type LookKvStore = {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
};

const memoryStore = (): LookKvStore => {
  const map = new Map<string, string>();
  return {
    getItem: (key) => map.get(key) ?? null,
    setItem: (key, value) => {
      map.set(key, value);
    },
    removeItem: (key) => {
      map.delete(key);
    },
  };
};

const defaultStore = (): LookKvStore => {
  if (typeof localStorage === 'undefined') {
    return memoryStore();
  }
  return localStorage;
};

export const stripFavorites = (settings: TemplateSettings): TemplateSettings => ({
  ...settings,
  favoritesList: [],
});

const parseLookOffsetValue = (value: unknown): number | null =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : null;

const parseLookOffset = (
  parsed: LookStorageRecord & { paletteIndex?: number | null }
): number | null =>
  parseLookOffsetValue(parsed.lookOffset) ?? parseLookOffsetValue(parsed.paletteIndex);

export const readLookStorage = (
  dateFrom: string,
  store: LookKvStore = defaultStore()
): LookStorageRecord | null => {
  const raw = store.getItem(lookStorageKey(dateFrom));
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as LookStorageRecord;
    if (!parsed || typeof parsed !== 'object') {
      store.removeItem(lookStorageKey(dateFrom));
      return null;
    }
    return {
      lockedTheme: parsed.lockedTheme ?? null,
      settings: parsed.settings ?? null,
      localBackground: Boolean(parsed.localBackground),
      lookOffset: parseLookOffset(parsed),
    };
  } catch {
    store.removeItem(lookStorageKey(dateFrom));
    return null;
  }
};

/**
 * Trả `false` khi không ghi được. `setItem` ném thật ngoài đời: Safari Private Browsing ném mọi
 * lần gọi, và quota đầy thì ném `QuotaExceededError`. Để lọt lỗi ra ngoài sẽ làm sập dialog qua
 * error boundary (gọi từ onChange) hoặc thành uncaught error trong timer debounce.
 */
export const writeLookStorage = (
  dateFrom: string,
  record: LookStorageRecord,
  store: LookKvStore = defaultStore()
): boolean => {
  try {
    store.setItem(lookStorageKey(dateFrom), JSON.stringify(record));
    return true;
  } catch {
    return false;
  }
};

export const clearLookStorage = (dateFrom: string, store: LookKvStore = defaultStore()): void => {
  store.removeItem(lookStorageKey(dateFrom));
};

export const coerceStoredSettings = (
  raw: unknown,
  seed: TemplateSettings
): TemplateSettings | null => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const stored = raw as TemplateSettings;
  if (!stored.general || !stored.font || !stored.menu) {
    return null;
  }
  return {
    ...seed,
    ...stored,
    general: {
      ...seed.general,
      ...stored.general,
      backgroundFile: stored.general.backgroundFile ?? seed.general.backgroundFile,
      localBackground: Boolean(stored.general.localBackground),
    },
    favoritesList: [],
  };
};

export const persistLookRecord = (args: {
  lockedTheme: string | null;
  localBackground: boolean;
  current: TemplateSettings;
  seed: TemplateSettings;
  lookOffset?: number | null;
}): LookStorageRecord | null => {
  const isSeed =
    JSON.stringify(stripFavorites(args.current)) === JSON.stringify(stripFavorites(args.seed));
  const lookOffset = args.lookOffset ?? null;
  const lookOverride = lookOffset != null && lookOffset !== 0;
  if (!args.lockedTheme && !args.localBackground && isSeed && !lookOverride) {
    return null;
  }
  return {
    lockedTheme: args.lockedTheme,
    settings: isSeed ? null : stripFavorites(args.current),
    localBackground: args.localBackground,
    lookOffset: lookOverride ? lookOffset : null,
  };
};

export const shouldPersistLook = (args: {
  lockedTheme: string | null;
  localBackground: boolean;
  current: TemplateSettings;
  seed: TemplateSettings;
  lookOffset?: number | null;
}): boolean => persistLookRecord(args) !== null;
